import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { apiGet, apiPost, ApiError } from "../../lib/api";
import MapPolygonDraw, { type LatLng } from "../../components/MapPolygonDraw";

type Channel = "push" | "sms" | "email";
type TargetType = "all" | "list" | "streets" | "polygon";

type DistributionList = { id: number; name: string; member_count: number };
type Street = { street: string; city: string };

export default function AdminNewMessage() {
  const navigate = useNavigate();
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [channels, setChannels] = useState<Channel[]>(["push"]);
  const [targetType, setTargetType] = useState<TargetType>("all");
  const [selectedListId, setSelectedListId] = useState<number | null>(null);
  const [selectedStreets, setSelectedStreets] = useState<string[]>([]);
  const [polygon, setPolygon] = useState<LatLng[]>([]);
  const [previewCount, setPreviewCount] = useState<number | null>(null);
  const [sendMode, setSendMode] = useState<"now" | "scheduled">("now");
  const [scheduledAt, setScheduledAt] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mapyApiKey = import.meta.env.VITE_MAPY_API_KEY ?? "";

  const { data: lists } = useQuery({
    queryKey: ["lists-for-message"],
    queryFn: () => apiGet<{ items: DistributionList[] }>("/api/admin/lists"),
    enabled: targetType === "list",
  });

  const { data: streets } = useQuery({
    queryKey: ["streets"],
    queryFn: () => apiGet<{ items: Street[] }>("/api/admin/contacts/streets"),
    enabled: targetType === "streets",
  });

  const buildTargetData = () => {
    if (targetType === "list") return { list_id: selectedListId };
    if (targetType === "streets") return { streets: selectedStreets };
    if (targetType === "polygon") return { polygon };
    return undefined;
  };

  const onPreview = async () => {
    setError(null);
    try {
      const res = await apiPost<{ count: number }>("/api/admin/messages/preview", {
        type: targetType,
        data: buildTargetData(),
      });
      setPreviewCount(res.count);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Náhled selhal.");
    }
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await apiPost("/api/admin/messages", {
        title,
        body,
        channels,
        target: { type: targetType, data: buildTargetData() },
        scheduledAt: sendMode === "scheduled" && scheduledAt ? new Date(scheduledAt).toISOString() : null,
      });
      navigate("/admin/messages");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Odeslání selhalo.");
    } finally {
      setSubmitting(false);
    }
  };

  const toggleChannel = (ch: Channel) =>
    setChannels((prev) => (prev.includes(ch) ? prev.filter((c) => c !== ch) : [...prev, ch]));

  const toggleStreet = (key: string) =>
    setSelectedStreets((prev) =>
      prev.includes(key) ? prev.filter((s) => s !== key) : [...prev, key],
    );

  return (
    <div>
      <h1>Nová zpráva</h1>

      {error && <div className="alert alert-error">{error}</div>}

      <form onSubmit={onSubmit}>
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="field">
            <label htmlFor="title">Titulek</label>
            <input id="title" value={title} onChange={(e) => setTitle(e.target.value)} required />
          </div>
          <div className="field">
            <label htmlFor="body">Text zprávy</label>
            <textarea
              id="body"
              rows={4}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              required
            />
          </div>

          <div className="field">
            <label>Kanály</label>
            {(["push", "sms", "email"] as Channel[]).map((ch) => (
              <label key={ch} style={{ display: "inline-flex", alignItems: "center", marginRight: 16 }}>
                <input
                  type="checkbox"
                  checked={channels.includes(ch)}
                  onChange={() => toggleChannel(ch)}
                  style={{ width: "auto", marginRight: 6 }}
                />
                {ch === "push" ? "Aplikace" : ch.toUpperCase()}
              </label>
            ))}
          </div>
        </div>

        <div className="card" style={{ marginBottom: 16 }}>
          <h3>Cílová skupina</h3>
          <div className="field">
            <label>Typ cílení</label>
            <select
              value={targetType}
              onChange={(e) => {
                setTargetType(e.target.value as TargetType);
                setPreviewCount(null);
              }}
            >
              <option value="all">Všichni ověření kontakti</option>
              <option value="list">Distribuční seznam</option>
              <option value="streets">Vybrané ulice</option>
              <option value="polygon">Polygon na mapě</option>
            </select>
          </div>

          {targetType === "list" && (
            <div className="field">
              <label>Seznam</label>
              <select
                value={selectedListId ?? ""}
                onChange={(e) => setSelectedListId(Number(e.target.value) || null)}
              >
                <option value="">— vyberte —</option>
                {lists?.items.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name} ({l.member_count})
                  </option>
                ))}
              </select>
            </div>
          )}

          {targetType === "streets" && (
            <div className="field">
              <label>Ulice (vyberte více)</label>
              <div style={{ maxHeight: 240, overflowY: "auto", border: "1px solid var(--color-border)", borderRadius: 8, padding: 12 }}>
                {streets?.items.map((s) => {
                  const key = s.street;
                  return (
                    <label key={`${s.street}-${s.city}`} style={{ display: "block", marginBottom: 4 }}>
                      <input
                        type="checkbox"
                        checked={selectedStreets.includes(key)}
                        onChange={() => toggleStreet(key)}
                        style={{ width: "auto", marginRight: 6 }}
                      />
                      {s.street}, {s.city}
                    </label>
                  );
                })}
                {streets?.items.length === 0 && <span className="muted">Žádné ulice</span>}
              </div>
            </div>
          )}

          {targetType === "polygon" && (
            <div className="field">
              <label>Nakreslete polygon na mapě (klikejte, dvojklik dokončí)</label>
              <MapPolygonDraw apiKey={mapyApiKey} onChange={setPolygon} />
            </div>
          )}

          <button type="button" className="secondary" onClick={onPreview}>
            Náhled počtu příjemců
          </button>
          {previewCount !== null && (
            <span style={{ marginLeft: 12 }} className="muted">
              Bude osloveno: <strong>{previewCount}</strong> kontaktů
            </span>
          )}
        </div>

        <div className="card" style={{ marginBottom: 16 }}>
          <h3>Odeslání</h3>
          <div className="field">
            <label style={{ display: "inline-flex", alignItems: "center", marginRight: 16 }}>
              <input
                type="radio"
                name="sendMode"
                checked={sendMode === "now"}
                onChange={() => setSendMode("now")}
                style={{ width: "auto", marginRight: 6 }}
              />
              Odeslat ihned
            </label>
            <label style={{ display: "inline-flex", alignItems: "center" }}>
              <input
                type="radio"
                name="sendMode"
                checked={sendMode === "scheduled"}
                onChange={() => setSendMode("scheduled")}
                style={{ width: "auto", marginRight: 6 }}
              />
              Načasovat odeslání
            </label>
          </div>
          {sendMode === "scheduled" && (
            <div className="field">
              <label htmlFor="scheduledAt">Datum a čas odeslání</label>
              <input
                id="scheduledAt"
                type="datetime-local"
                value={scheduledAt}
                onChange={(e) => setScheduledAt(e.target.value)}
                required
                style={{ maxWidth: 280 }}
              />
            </div>
          )}
        </div>

        <button type="submit" disabled={submitting || (sendMode === "scheduled" && !scheduledAt)}>
          {submitting
            ? "Odesílám…"
            : sendMode === "scheduled"
              ? "Naplánovat zprávu"
              : "Odeslat zprávu"}
        </button>
      </form>
    </div>
  );
}
